import { sha256 } from './identity';

export const EDID_NAMES = ['edid-dp1.txt', 'edid-dp2.txt', 'edid-dvi-d1.txt', 'edid-dvi-d2.txt'];
export const FILE_NAMES = ['gamma.txt', 'dimming.txt', 'monitorinfo.txt', 'paneltimings.txt', 'wave_cfg.txt', ...EDID_NAMES];
const HEADER = [0, 255, 255, 255, 255, 255, 255, 0];
const integer = (value, low, high, label) => {
  if (!Number.isSafeInteger(value) || value < low || value > high) throw new Error(`Invalid ${label}.`);
  return value;
};
const ascii = (value, maximum, label) => {
  if (typeof value !== 'string' || !value.length || value.length > maximum || value.trim() !== value || !/^[ -~]+$/.test(value)) throw new Error(`Invalid ${label}.`);
  return value;
};
const bytes = value => Uint8Array.from(value, character => character.charCodeAt(0));
function text(data) {
  if (!(data instanceof Uint8Array) || !data.length || data.length > 1024 * 1024 || data.some(value => value === 0 || value > 127)) throw new Error('Expected bounded ASCII file bytes.');
  return Array.from(data, value => String.fromCharCode(value)).join('');
}
export function encodeServiceTag(tag) {
  if (typeof tag !== 'string' || !/^[A-I][0-9]{9}$/.test(tag)) throw new Error('Monitor service tag requires A–I and nine digits.');
  return integer(Number(`${tag.charCodeAt(0) - 64}${tag.slice(1)}`), 0, 0xffffffff, '32-bit monitor serial');
}
export function parseEdid(data) {
  if (!(data instanceof Uint8Array)) throw new Error('EDID must contain bytes.');
  let block;
  if (data.length === 128 && HEADER.every((value, index) => data[index] === value)) block = data.slice();
  else {
    const rows = [];
    for (const line of text(data).split(/\r?\n/)) {
      if (!line.includes('|')) continue;
      const parts = line.split('|'), values = parts[1]?.trim().split(/\s+/);
      if (parts.length !== 2 || !/^\s*[0-9a-f]{2}\s*$/i.test(parts[0]) || parseInt(parts[0], 16) !== rows.length * 16
        || values.length !== 16 || values.some(value => !/^[a-f0-9]{2}$/i.test(value))) throw new Error('EDID needs eight ordered 16-byte Phoenix rows.');
      rows.push(values.map(value => parseInt(value, 16)));
    }
    if (rows.length !== 8) throw new Error('EDID needs exactly 128 bytes.');
    block = Uint8Array.from(rows.flat());
  }
  if (block.length !== 128 || !HEADER.every((value, index) => block[index] === value) || block[126] !== 0 || block.reduce((sum, value) => sum + value, 0) % 256) throw new Error('Invalid EDID header, extension count or checksum.');
  return block;
}
const hex = value => value.toString(16).toUpperCase().padStart(2, '0');
export function edidText(data) {
  const block = parseEdid(data), lines = ['EDID BYTES:', `     ${Array.from({ length: 16 }, (_, index) => hex(index)).join(' ')}`, `     ${Array(16).fill('--').join(' ')}`];
  for (let offset = 0; offset < 128; offset += 16) lines.push(`${hex(offset)} | ${Array.from(block.slice(offset, offset + 16), hex).join(' ')}`);
  return bytes(lines.join('\r\n'));
}
function xy(value) {
  if (!Array.isArray(value) || value.length !== 3 || value.some(number => typeof number !== 'number' || !Number.isFinite(number))
    || value[0] < 0 || value[0] > 1 || value[1] <= 0 || value[1] > 1 || value[0] + value[1] > 1.000001 || value[2] <= 0) throw new Error('Invalid measured EDID chromaticity.');
  return value.slice(0, 2);
}
export function patchEdid(base, { serviceTag, monitorType, date, primaries, white }) {
  const block = parseEdid(base), serial = encodeServiceTag(serviceTag), model = ascii(monitorType, 13, 'monitor type');
  const manufactured = new Date(`${date}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(manufactured.getTime()) || manufactured.toISOString().slice(0, 10) !== date || manufactured.getUTCFullYear() < 1990 || manufactured.getUTCFullYear() > 2245) throw new Error('Invalid EDID manufacture date.');
  if (!Array.isArray(primaries) || primaries.length !== 3) throw new Error('Three native primaries are required.');
  const codes = [...primaries.flatMap(xy), ...xy(white)].map(value => Math.min(1023, Math.floor(value * 1024 + .5)));
  new DataView(block.buffer).setUint32(12, serial, true);
  const thursday = new Date(manufactured); thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  block[16] = Math.ceil((((thursday - Date.UTC(thursday.getUTCFullYear(), 0, 1)) / 86400000) + 1) / 7);
  block[17] = manufactured.getUTCFullYear() - 1990;
  block[25] = codes.slice(0, 4).reduce((sum, value, index) => sum + ((value & 3) << (6 - index * 2)), 0);
  block[26] = codes.slice(4).reduce((sum, value, index) => sum + ((value & 3) << (6 - index * 2)), 0);
  block.set(codes.map(value => value >> 2), 27);
  if ([0, 0, 0, 252, 0].some((value, index) => block[90 + index] !== value)) throw new Error('EDID descriptor three must identify the monitor name.');
  block.set(bytes(`${model}\n${' '.repeat(13)}`.slice(0, 13)), 95);
  block[127] = (256 - block.slice(0, 127).reduce((sum, value) => sum + value, 0) % 256) % 256;
  return parseEdid(block);
}
function gammaFile(channels) {
  if (!channels || Object.keys(channels).sort().join() !== 'B,G,R') throw new Error('RGB gamma channels are required.');
  for (const channel of 'RGB') {
    const values = channels[channel];
    if (!Array.isArray(values) || values.length !== 256 || values.some((value, index) => !Number.isInteger(value) || value < 0 || value > 255 || (index > 0 && value < values[index - 1]))) throw new Error('Gamma requires 256 monotone byte values per channel.');
  }
  return bytes(['#Gamma compensation table', '#Input|B|G|R', ...Array.from({ length: 256 }, (_, index) => `${index};${channels.B[index]};${channels.G[index]};${channels.R[index]}`), ''].join('\r\n'));
}
function dimmingFile(base, backlight) {
  integer(backlight, 1, 100, 'backlight'); let count = 0;
  const lines = text(base).split(/\r?\n/); if (lines[lines.length - 1] === '') lines.pop();
  const transformed = lines.map(line => {
    if (!line.trim() || line.trimStart().startsWith('#')) return line;
    const fields = line.trim().split(';');
    if (fields.length !== 7 || fields[6] !== '' || fields.slice(0, 6).some(value => !/^\d+$/.test(value.trim()))) throw new Error('Invalid dimming row.');
    const row = fields.slice(0, 6).map(Number);
    if (row[0] !== count++) throw new Error('Dimming indices must be 0–100 in order.');
    integer(row[0], 0, 100, 'dimming index'); integer(row[1], 1, 65535, 'dimming period'); integer(row[2], 1, row[1], 'ON time');
    row.slice(3).forEach(value => integer(value, 0, 255, 'dimming byte'));
    const total = row[2] * backlight, quotient = Math.floor(total / 100), remainder = total % 100;
    row[2] = quotient + Number(remainder > 50 || (remainder === 50 && quotient % 2 === 1));
    if (row[2] < 1) throw new Error('Backlight scaling would create zero ON time.');
    return `${row.join(';')};`;
  });
  if (count !== 101) throw new Error('Dimming requires 101 rows.');
  return bytes(`${transformed.join('\r\n')}\r\n`);
}
function validateTiming(data) {
  const rows = text(data).split(/\r?\n/).filter(line => line.trim() && !line.trimStart().startsWith('#'));
  if (rows.length !== 1) throw new Error('One panel timing row is required.');
  const fields = rows[0].trim().split(';');
  if (fields.length !== 10 || fields[9] !== '' || fields.slice(0, 9).some(value => !/^\d+$/.test(value.trim()))) throw new Error('Invalid panel timing.');
  const values = fields.slice(0, 9).map(Number); values.forEach(value => integer(value, 0, 65535, 'timing value'));
  if (!values[0] || !values[1] || values[7] > 8) throw new Error('Invalid timing dimensions or LVDS clock.');
}
function validateCfg(data) {
  const seen = new Set();
  for (const line of text(data).split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith('#')) continue;
    const pair = line.split('='), key = pair[0]?.trim(), value = pair[1]?.trim();
    if (pair.length !== 2 || !['ETHERNET', 'DEBUG_CONSOLE', 'SAAB_CONFIG', 'SAAB_POT_SCALE', 'DIMMING_TOUCH_SLIDER'].includes(key) || !['YES', 'NO'].includes(value) || seen.has(key)) throw new Error('Invalid or duplicate base configuration setting.');
    seen.add(key);
  }
  if (!seen.size) throw new Error('Base configuration is empty.');
}
export function validateFileset(files) {
  if (!files || Object.keys(files).sort().join('|') !== [...FILE_NAMES].sort().join('|')) throw new Error('A complete named nine-file fileset is required.');
  const rows = text(files['gamma.txt']).split(/\r?\n/); if (rows[rows.length - 1] === '') rows.pop();
  if (rows.length !== 258 || rows[0] !== '#Gamma compensation table' || rows[1] !== '#Input|B|G|R') throw new Error('Invalid gamma header or row count.');
  const channels = { B: [], G: [], R: [] };
  rows.slice(2).forEach((row, index) => {
    const values = row.split(';');
    if (values.length !== 4 || values.some(value => !/^\d+$/.test(value)) || Number(values[0]) !== index) throw new Error('Invalid gamma row.');
    ['B', 'G', 'R'].forEach((channel, offset) => channels[channel].push(Number(values[offset + 1])));
  });
  gammaFile(channels); dimmingFile(files['dimming.txt'], 100); validateTiming(files['paneltimings.txt']); validateCfg(files['wave_cfg.txt']);
  const blocks = EDID_NAMES.map(name => { if (!text(files[name]).startsWith('EDID BYTES:')) throw new Error('Monitor EDIDs require Phoenix text.'); return parseEdid(files[name]); });
  if (blocks.some(block => block.some((value, index) => value !== blocks[0][index]))) throw new Error('Port EDIDs differ.');
  const info = {};
  for (const line of text(files['monitorinfo.txt']).split(/\r?\n/)) {
    if (!line || line.startsWith('#')) continue;
    const pair = line.split('='); if (pair.length !== 2 || Object.prototype.hasOwnProperty.call(info, pair[0])) throw new Error('Invalid monitor information.'); info[pair[0]] = pair[1];
  }
  if (Object.keys(info).sort().join() !== 'MONITOR_SERIAL,MONITOR_TYPE,SOFTWARE_ID' || !/^\d+(?:\.\d+){3}$/.test(info.SOFTWARE_ID) || info.SOFTWARE_ID.split('.').some(value => Number(value) > 255)) throw new Error('Invalid monitor identity fields.');
  ascii(info.MONITOR_TYPE, 13, 'monitor type');
  if (new DataView(blocks[0].buffer).getUint32(12, true) !== encodeServiceTag(info.MONITOR_SERIAL)
    || [0, 0, 0, 252, 0].some((value, index) => blocks[0][90 + index] !== value)
    || String.fromCharCode(...blocks[0].slice(95, 108)).split('\n')[0].trimEnd() !== info.MONITOR_TYPE) throw new Error('EDID and monitor information identities differ.');
  return true;
}
export function buildFileset({ monitorType, assets, applicationVersion = '0.2.0.0' }, calibration) {
  if (!/^\d+(?:\.\d+){3}$/.test(applicationVersion) || applicationVersion.split('.').some(value => Number(value) > 255)) throw new Error('Invalid fileset software version.');
  ascii(monitorType, 13, 'monitor type'); validateTiming(assets.baseTiming); validateCfg(assets.baseCfg);
  const edid = edidText(patchEdid(assets.baseEdid, { ...calibration, monitorType }));
  const files = { 'gamma.txt': gammaFile(calibration.channels), 'dimming.txt': dimmingFile(assets.baseDimming, calibration.backlightPercent),
    'monitorinfo.txt': bytes(`#Monitor identification data\r\nSOFTWARE_ID=${applicationVersion}\r\nMONITOR_SERIAL=${calibration.serviceTag}\r\nMONITOR_TYPE=${monitorType}`),
    'paneltimings.txt': assets.baseTiming.slice(), 'wave_cfg.txt': assets.baseCfg.slice(), ...Object.fromEntries(EDID_NAMES.map(name => [name, edid.slice()])) };
  validateFileset(files); return files;
}
export const describeFiles = async files => Promise.all(Object.keys(files).sort().map(async name => ({ name, sha256: await sha256(files[name]), bytes: files[name].length })));
export async function verifyFilesReadback(files, selectedFiles) {
  validateFileset(files);
  const selected = Array.from(selectedFiles || []), names = selected.map(file => file.name);
  if (names.length !== FILE_NAMES.length || new Set(names).size !== names.length || names.some(name => !FILE_NAMES.includes(name))) throw new Error('Select exactly the nine copied files, without duplicates.');
  for (const file of selected) {
    if (file.size !== files[file.name].length || await sha256(new Uint8Array(await file.arrayBuffer())) !== await sha256(files[file.name])) throw new Error(`Copied file readback differs: ${file.name}.`);
  }
  return true;
}
export async function verifyEdidReadback(expected, captured) {
  if (captured.length > 16384) throw new Error('EDID capture exceeds 16 KiB.');
  const wanted = parseEdid(expected), actual = parseEdid(captured);
  if (actual.some((value, index) => value !== wanted[index])) throw new Error('Independently captured EDID differs from the generated 128-byte block.');
  return { matched: true, bytes: 128, sha256: await sha256(actual) };
}

export function makeFilesetZip(files) {
  validateFileset(files);
  const crc32 = data => {
    let crc = 0xffffffff;
    for (const value of data) { crc ^= value; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); }
    return (crc ^ 0xffffffff) >>> 0;
  };
  const local = [], directory = []; let offset = 0, directorySize = 0;
  for (const name of [...FILE_NAMES].sort()) {
    const filename = bytes(`fileset/${name}`), data = files[name], crc = crc32(data), header = new Uint8Array(30 + filename.length), view = new DataView(header.buffer);
    view.setUint32(0, 0x04034b50, true); view.setUint16(4, 20, true); view.setUint16(12, 33, true);
    view.setUint32(14, crc, true); view.setUint32(18, data.length, true); view.setUint32(22, data.length, true); view.setUint16(26, filename.length, true); header.set(filename, 30);
    const central = new Uint8Array(46 + filename.length), entry = new DataView(central.buffer);
    entry.setUint32(0, 0x02014b50, true); entry.setUint16(4, 20, true); entry.setUint16(6, 20, true); entry.setUint16(14, 33, true);
    entry.setUint32(16, crc, true); entry.setUint32(20, data.length, true); entry.setUint32(24, data.length, true); entry.setUint16(28, filename.length, true); entry.setUint32(42, offset, true); central.set(filename, 46);
    local.push(header, data); directory.push(central); offset += header.length + data.length; directorySize += central.length;
  }
  const end = new Uint8Array(22), view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true); view.setUint16(8, 9, true); view.setUint16(10, 9, true); view.setUint32(12, directorySize, true); view.setUint32(16, offset, true);
  const output = new Uint8Array(offset + directorySize + end.length); let cursor = 0;
  for (const section of [...local, ...directory, end]) { output.set(section, cursor); cursor += section.length; }
  return output;
}
